/**
 * Supplier Detail Page
 *
 * Refactored to use modular sub-components for improved maintainability.
 * All state management is handled by the useSupplierDetail hook.
 */

import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Loader2,
  ArrowLeft,
  Star,
  CheckCircle2,
  Package,
  Mail,
  Power,
  Camera,
  ShieldAlert,
  ShieldCheck,
  Shield,
  ShieldX,
  ExternalLink,
} from 'lucide-react';
import OTDDrilldownModal from '../components/drilldown/OTDDrilldownModal';
import { getRiskLevelStyle } from './suppliers-list/utils';
import { suppliersAPI } from '../lib/api';
import { useFormatters } from '../contexts/PreferencesContext';
import { useMenuSettings } from '../contexts/MenuSettingsContext';
import { toast } from 'sonner';

// Import modular components
import {
  useSupplierDetail,
  OverviewTab,
  PerformanceTab,
  OrdersTab,
  ContractsTab,
  QualityTab,
  UsersTab,
  UserModal,
  ActivityTab,
  CreditScoreTab,
  QuestionnairesTab,
  SettingsTab,
  DocumentsTab,
} from './supplier-detail';
import GroupStructureTab from './supplier-detail/GroupStructureTab';

export default function SupplierDetail() {
  const navigate = useNavigate();
  const { formatCurrency: formatCurrencyFromHook } = useFormatters();
  const { supplierProfileLayout } = useMenuSettings();

  // CR-5557: drill-through modal opened from the OTD KPI tile so users can
  // validate which GRNs are included in the rate.
  const [otdDrilldownOpen, setOtdDrilldownOpen] = useState(false);

  const {
    // State
    supplier,
    orders,
    ncrs,
    users,
    loading,
    ordersLoading,
    ncrsLoading,
    usersLoading,
    error,
    activeTab,
    selectedUser,
    userModalOpen,
    userModalTab,
    userSitePermissions,
    permissionsLoading,
    savingPermission,
    impersonating,
    otifSettings,
    otifLoading,
    otifSaving,

    // Actions
    setActiveTab,
    handleOpenUserModal,
    handleCloseUserModal,
    handleToggleSiteLink,
    handleUpdatePermission,
    setUserSearch,
    handleOtifSettingsUpdate,
    setOtifSettings,
    handleUpdateProfile,
    handleUpdateCompany,
    handleUploadLogo,
    handleImpersonate,
    setUserModalTab,
    fetchSupplierData,
    fetchUsers,

    // Computed
    filteredUsers,
    isHelixAdmin,
    isCustomer,
    isOwnCompany,
    userSearch,
  } = useSupplierDetail();

  const [toggling, setToggling] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const logoInputRef = useRef<HTMLInputElement>(null);

  const canEditLogo = isHelixAdmin || isCustomer || isOwnCompany;

  const onLogoFileChosen = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please choose an image file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Logo must be 5MB or smaller');
      return;
    }
    try {
      setUploadingLogo(true);
      await handleUploadLogo(file);
      toast.success('Logo updated');
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to upload logo';
      toast.error(message);
    } finally {
      setUploadingLogo(false);
    }
  };

  // Format currency helper
  const formatCurrency = (value: string | number) => {
    const num = typeof value === 'string' ? parseFloat(value) : value;
    return formatCurrencyFromHook(num);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (error || !supplier) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">Error: {error || 'Supplier not found'}</p>
          <Button onClick={() => navigate('/suppliers')} variant="outline" className="mt-4">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Suppliers
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-4">
          <Button variant="outline" size="icon" onClick={() => navigate('/suppliers')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          {(() => {
            const logo = (supplier.company_details as unknown as Record<string, unknown>)?.['logo_url'] as string | undefined;
            return (
              <div className="relative w-24 h-24 rounded-lg border bg-white flex items-center justify-center overflow-hidden shrink-0 p-2 group">
                {logo ? (
                  <img src={logo} alt={supplier.company_name} className="max-w-full max-h-full object-contain" />
                ) : (
                  <span className="text-3xl font-semibold text-muted-foreground">
                    {supplier.company_name.trim().charAt(0).toUpperCase() || '?'}
                  </span>
                )}
                {canEditLogo && (
                  <>
                    <input
                      ref={logoInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={onLogoFileChosen}
                    />
                    <button
                      type="button"
                      aria-label="Change company logo"
                      onClick={() => logoInputRef.current?.click()}
                      disabled={uploadingLogo}
                      className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/50 text-white opacity-0 group-hover:opacity-100 transition disabled:opacity-60"
                    >
                      {uploadingLogo ? (
                        <Loader2 className="h-5 w-5 animate-spin" />
                      ) : (
                        <>
                          <Camera className="h-5 w-5 mr-1" />
                          <span className="text-xs font-medium">Change</span>
                        </>
                      )}
                    </button>
                  </>
                )}
              </div>
            );
          })()}
          <div>
            <div className="flex items-center gap-3 mb-2">
              {supplier.is_preferred && (
                <Star className="h-5 w-5 text-yellow-500 fill-yellow-500" />
              )}
              <h1 className="text-3xl font-bold tracking-tight">{supplier.company_name}</h1>
              <Badge
                className={
                  (supplier as unknown as Record<string, unknown>)['onboarding_status'] === 'approved' || supplier.is_approved
                    ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                    : (supplier as unknown as Record<string, unknown>)['onboarding_status'] === 'not_approved'
                    ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                    : (supplier as unknown as Record<string, unknown>)['onboarding_status'] === 'pending_initial'
                    ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                    : (supplier as unknown as Record<string, unknown>)['onboarding_status'] === 'invited'
                    ? 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200'
                    : (supplier as unknown as Record<string, unknown>)['onboarding_status'] === 'due_diligence'
                    ? 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200'
                    : 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                }
              >
                {(() => {
                  const status = (supplier as unknown as Record<string, unknown>)['onboarding_status'] as string;
                  switch (status) {
                    case 'approved': return 'Approved';
                    case 'not_approved': return 'Not Approved';
                    case 'pending_initial': return 'Pending Initial Approval';
                    case 'invited': return 'Invited';
                    case 'due_diligence': return 'Due Diligence';
                    case 'draft': return 'Draft';
                    default: return supplier.is_approved ? 'Approved' : 'Under Review';
                  }
                })()}
              </Badge>
              {/* CR-4771: Risk Level badge — same styling as the cards */}
              {supplier.risk_level && supplier.risk_level !== 'none' && (() => {
                const style = getRiskLevelStyle(supplier.risk_level);
                const Icon = style.icon === 'critical' ? ShieldX
                  : style.icon === 'high' ? ShieldAlert
                  : style.icon === 'low' ? ShieldCheck
                  : Shield;
                return (
                  <Badge className={`text-xs font-medium px-2.5 py-1 ${style.badgeColor}`}>
                    <Icon className="h-3.5 w-3.5 mr-1" />
                    {style.label}
                  </Badge>
                );
              })()}
              {/* CR-4771: Credit Score badge — banding matches SupplierCard */}
              {typeof supplier.latest_credit_score === 'number' && (() => {
                const score = supplier.latest_credit_score;
                const band = score >= 800
                  ? { label: 'Excellent', cls: 'bg-green-50 text-green-700 border-green-200' }
                  : score >= 700
                    ? { label: 'Good', cls: 'bg-blue-50 text-blue-700 border-blue-200' }
                    : score >= 600
                      ? { label: 'Fair', cls: 'bg-amber-50 text-amber-700 border-amber-200' }
                      : { label: 'Poor', cls: 'bg-red-50 text-red-700 border-red-200' };
                return (
                  <Badge className={`text-xs font-medium px-2.5 py-1 ${band.cls}`} title={`Credit Score ${score} — ${band.label}`}>
                    Credit {score}
                  </Badge>
                );
              })()}
              {/* CR-4056: Active/Inactive Toggle */}
              <button
                onClick={async () => {
                  try {
                    setToggling(true);
                    const newState = !supplier.company_details.is_active;
                    await suppliersAPI.toggleSupplierActive(supplier.company, newState);
                    toast.success(`Supplier ${newState ? 'activated' : 'deactivated'} successfully`);
                    await fetchSupplierData();
                  } catch (err: unknown) {
                    const error = err as { response?: { data?: { detail?: string } } };
                    toast.error(error.response?.data?.detail || 'Failed to update supplier status');
                  } finally {
                    setToggling(false);
                  }
                }}
                disabled={toggling}
                className={`ml-2 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border transition-colors disabled:opacity-50 ${
                  supplier.company_details.is_active
                    ? 'bg-green-50 text-green-700 border-green-200 hover:bg-green-100'
                    : 'bg-red-50 text-red-700 border-red-200 hover:bg-red-100'
                }`}
                title={`Click to ${supplier.company_details.is_active ? 'deactivate' : 'activate'} supplier`}
              >
                {toggling ? <Loader2 className="h-3 w-3 animate-spin" /> : <Power className="h-3 w-3" />}
                {supplier.company_details.is_active ? 'Active' : 'Inactive'}
              </button>
            </div>
            <p className="text-muted-foreground">
              {supplier.company_details.company_type === 'supplier' ? 'Supplier' : 'Company'} Profile
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => {
              const email = supplier.company_details.primary_email;
              if (email) {
                window.open(`mailto:${email}`, '_blank');
              } else {
                toast.error('No contact email available for this supplier');
              }
            }}
          >
            <Mail className="h-4 w-4 mr-2" />
            Contact
          </Button>
        </div>
      </div>

      {/* Key Metrics Cards */}
      {(supplierProfileLayout.stats.otd || supplierProfileLayout.stats.total_orders) && (
        <div className={`grid gap-4 ${supplierProfileLayout.stats.otd && supplierProfileLayout.stats.total_orders ? 'md:grid-cols-2' : 'md:grid-cols-1'}`}>
          {supplierProfileLayout.stats.otd && (
            <Card
              className="cursor-pointer hover:shadow-md transition-shadow group"
              onClick={() => setOtdDrilldownOpen(true)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && setOtdDrilldownOpen(true)}
              title="Click to view the GRNs included in this rate"
            >
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4" />
                  On-Time Delivery
                  <ExternalLink className="h-3 w-3 ml-auto text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">
                  {supplier.on_time_delivery_rate.toFixed(1)}%
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {/* CR-5557: subtitle shows OTD-passed (on-time + early)
                      so it matches the percentage above. The breakdown
                      (strict on-time / early / late) is in the Performance
                      tab and the drill-through. */}
                  {(supplier.ontime_count ?? 0) + (supplier.early_count ?? 0)} of {supplier.grn_count} GRNs
                  {supplier.otd_timeframe_label && (
                    <span className="ml-1">· {supplier.otd_timeframe_label}</span>
                  )}
                </p>
              </CardContent>
            </Card>
          )}
          {supplierProfileLayout.stats.total_orders && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Package className="h-4 w-4" />
                  Total Orders
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">{supplier.total_orders}</div>
                <p className="text-xs text-muted-foreground mt-1">
                  {supplier.completed_orders} completed
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList>
          <TabsTrigger value="overview">Company Overview</TabsTrigger>
          {supplierProfileLayout.tabs.performance && <TabsTrigger value="performance">Performance</TabsTrigger>}
          {supplierProfileLayout.tabs.orders && <TabsTrigger value="orders">Orders</TabsTrigger>}
          {supplierProfileLayout.tabs.contracts && <TabsTrigger value="contracts">Contracts</TabsTrigger>}
          {supplierProfileLayout.tabs.quality && <TabsTrigger value="quality">Quality</TabsTrigger>}
          {supplierProfileLayout.tabs.questionnaires && <TabsTrigger value="questionnaires">Questionnaires</TabsTrigger>}
          {supplierProfileLayout.tabs.credit_scores && <TabsTrigger value="credit-scores">Credit Scores</TabsTrigger>}
          {supplierProfileLayout.tabs.users && <TabsTrigger value="users">Users</TabsTrigger>}
          {/* Defaults to visible when an existing tenant's layout JSON
              predates the documents key. Bracket access because documents
              isn't an explicit field on SupplierProfileLayout.tabs. */}
          {(supplierProfileLayout.tabs['documents'] ?? true) && <TabsTrigger value="documents">Documents</TabsTrigger>}
          {/* CR-4773: Renamed to match the card content ("Communication Audit Log") */}
          {supplierProfileLayout.tabs.activity_log && <TabsTrigger value="activity">Communication Log</TabsTrigger>}
          <TabsTrigger value="settings">Settings</TabsTrigger>
          {supplierProfileLayout.tabs.group_structure && <TabsTrigger value="group-structure">Group Structure</TabsTrigger>}
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-4">
          <OverviewTab
            supplier={supplier}
            formatCurrency={formatCurrency}
            canEdit={isHelixAdmin || isCustomer}
            onUpdateProfile={handleUpdateProfile}
            onUpdateCompany={handleUpdateCompany}
          />
        </TabsContent>

        {/* Performance Tab */}
        <TabsContent value="performance" className="space-y-4">
          <PerformanceTab supplier={supplier} />
        </TabsContent>

        {/* Orders Tab */}
        <TabsContent value="orders" className="space-y-4">
          <OrdersTab
            supplier={supplier}
            orders={orders}
            ordersLoading={ordersLoading}
            formatCurrency={formatCurrency}
          />
        </TabsContent>

        {/* Contracts Tab */}
        <TabsContent value="contracts" className="space-y-4">
          <ContractsTab supplier={supplier} />
        </TabsContent>

        {/* Quality Tab */}
        <TabsContent value="quality" className="space-y-4">
          <QualityTab supplier={supplier} ncrs={ncrs} ncrsLoading={ncrsLoading} />
        </TabsContent>

        {/* Questionnaires Tab */}
        <TabsContent value="questionnaires" className="space-y-4">
          <QuestionnairesTab supplierId={supplier.company} supplierName={supplier.company_name} />
        </TabsContent>

        {/* Credit Scores Tab */}
        <TabsContent value="credit-scores" className="space-y-4">
          <CreditScoreTab supplier={supplier} />
        </TabsContent>

        {/* Users Tab */}
        <TabsContent value="users" className="space-y-4">
          <UsersTab
            users={users}
            filteredUsers={filteredUsers}
            usersLoading={usersLoading}
            userSearch={userSearch}
            impersonating={impersonating}
            isHelixAdmin={isHelixAdmin}
            isCustomer={isCustomer}
            companyId={supplier?.company || 0}
            companyName={supplier?.company_name || ''}
            groupId={supplier?.company_details?.group}
            onUserSearchChange={setUserSearch}
            onOpenUserModal={handleOpenUserModal}
            onImpersonate={handleImpersonate}
            onRefreshUsers={fetchUsers}
          />
        </TabsContent>

        {/* Documents Tab */}
        <TabsContent value="documents" className="space-y-4">
          <DocumentsTab supplier={supplier} />
        </TabsContent>

        {/* CR-4477: Activity / Traceability Log Tab */}
        <TabsContent value="activity" className="space-y-4">
          <ActivityTab supplier={supplier} />
        </TabsContent>

        {/* Settings Tab */}
        <TabsContent value="settings" className="space-y-4">
          <SettingsTab
            supplier={supplier}
            otifSettings={otifSettings}
            otifLoading={otifLoading}
            otifSaving={otifSaving}
            isHelixAdmin={isHelixAdmin}
            isCustomer={isCustomer}
            onOtifSettingsUpdate={handleOtifSettingsUpdate}
            setOtifSettings={setOtifSettings}
            onUpdateProfile={handleUpdateProfile}
          />
        </TabsContent>

        {/* Group Structure Tab */}
        <TabsContent value="group-structure" className="space-y-4">
          <GroupStructureTab
            supplier={supplier}
            isHelixAdmin={isHelixAdmin}
            isCustomer={isCustomer}
          />
        </TabsContent>
      </Tabs>

      {/* CR-5557: OTD KPI tile drill-through */}
      <OTDDrilldownModal
        isOpen={otdDrilldownOpen}
        onClose={() => setOtdDrilldownOpen(false)}
        contextLabel={supplier?.company_name}
        filters={{ supplier: supplier?.company }}
      />

      {/* User Detail Modal */}
      <UserModal
        open={userModalOpen}
        onOpenChange={handleCloseUserModal}
        selectedUser={selectedUser}
        userModalTab={userModalTab}
        onTabChange={setUserModalTab}
        userSitePermissions={userSitePermissions}
        permissionsLoading={permissionsLoading}
        savingPermission={savingPermission}
        impersonating={impersonating}
        isHelixAdmin={isHelixAdmin}
        isCustomer={isCustomer}
        contextCompanyName={supplier?.company_name}
        onToggleSiteLink={handleToggleSiteLink}
        onUpdatePermission={handleUpdatePermission}
        onImpersonate={handleImpersonate}
      />
    </div>
  );
}
